import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Mail, Linkedin, ArrowUpRight } from "lucide-react";

const PROFILE = {
  email: "rishikeshn@me.com",
  linkedin: "https://www.linkedin.com/in/shambomahadeva/",
};

const EXPERTISE = [
  "Strategic Communications",
  "Brand Narrative",
  "AI-Enhanced Storytelling",
  "Digital Transformation",
  "Creative & Film Direction",
  "Cross-Functional Leadership",
];

export default function Teaser() {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    document.title = "I'm coming — Rishikesh N";
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const istTime = time.toLocaleTimeString("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  return (
    <div className="min-h-screen bg-[var(--bg)] text-white overflow-hidden relative">
      <style>{`
        @keyframes teaserGlow {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.55; }
          50% { transform: translate(40px, -30px) scale(1.1); opacity: 0.85; }
        }
        @keyframes scrollIn {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        .teaser-glow {
          position: absolute;
          width: 800px;
          height: 800px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(212,175,55,0.18) 0%, rgba(212,175,55,0) 65%);
          filter: blur(60px);
          animation: teaserGlow 12s ease-in-out infinite;
          pointer-events: none;
        }
        .teaser-marquee {
          display: flex; width: max-content;
          animation: scrollIn 80s linear infinite;
        }
        .teaser-headline {
          font-family: 'Clash Display', serif;
          font-weight: 500;
          letter-spacing: -0.04em;
          line-height: 0.85;
        }
        .teaser-blink {
          animation: blink 1.2s step-end infinite;
        }
        @keyframes blink { 50% { opacity: 0; } }
      `}</style>

      <div className="grain" />
      <div className="teaser-glow" style={{ top: "-200px", left: "-200px" }} />
      <div
        className="teaser-glow"
        style={{
          bottom: "-200px",
          right: "-200px",
          animationDelay: "-6s",
          background:
            "radial-gradient(circle, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 60%)",
        }}
      />

      {/* Top bar */}
      <header
        data-testid="teaser-top-bar"
        className="relative z-20 max-w-7xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between"
      >
        <a
          href="#"
          data-testid="teaser-logo"
          className="font-display text-xl md:text-2xl tracking-tight uppercase"
        >
          Rishikesh<span className="text-gold">.</span>
        </a>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[var(--text-dim)]">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-gold teaser-blink" />
          BLR · {istTime} IST
        </div>
      </header>

      {/* Main */}
      <main className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-24 min-h-[calc(100vh-180px)] flex flex-col justify-center">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="flex items-center gap-3 mb-10 text-xs font-body uppercase tracking-[0.35em] text-[var(--text-muted)]"
          data-testid="teaser-eyebrow"
        >
          <span className="h-px w-12 bg-gold" />
          A New Chapter — 2026
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.1 }}
          className="teaser-headline text-[22vw] md:text-[16vw] lg:text-[13vw]"
          data-testid="teaser-headline"
        >
          I'm <span className="text-gold italic">coming.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.5 }}
          className="mt-10 max-w-2xl text-lg md:text-xl text-[var(--text-muted)] font-body leading-relaxed"
          data-testid="teaser-tagline"
        >
          Strategic communications consultant. 20+ years across journalism, broadcasting,
          advertising and AI-led storytelling.
          <span className="text-white"> The new site arrives shortly.</span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.75 }}
          className="mt-12 flex flex-wrap items-center gap-4"
        >
          <a
            href={`mailto:${PROFILE.email}?subject=Reserve%20a%20conversation%20with%20Rishikesh`}
            data-testid="teaser-cta-email"
            className="group inline-flex items-center gap-3 bg-gold text-black px-7 py-4 text-sm font-body uppercase tracking-[0.2em] hover:bg-white transition-colors duration-300"
          >
            <Mail size={16} />
            Reserve a Conversation
            <ArrowUpRight
              size={16}
              className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
            />
          </a>
          <a
            href={PROFILE.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="teaser-cta-linkedin"
            className="group inline-flex items-center gap-3 border border-line text-white px-7 py-4 text-sm font-body uppercase tracking-[0.2em] hover:border-gold hover:text-gold transition-colors duration-300"
          >
            <Linkedin size={16} />
            Follow on LinkedIn
          </a>
        </motion.div>

        {/* Stats / proof */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.1 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-2 max-w-2xl"
          data-testid="teaser-stats"
        >
          {[
            { n: "20+", l: "Years Experience" },
            { n: "700+", l: "Projects & Campaigns" },
            { n: null, l: "Multi-Industry" },
            { n: null, l: "Global Exposure" },
          ].map((s, i) => (
            <div
              key={i}
              className={i > 0 ? "md:border-l md:border-[var(--line)] md:pl-5" : ""}
            >
              {s.n ? (
                <>
                  <div className="font-display text-3xl md:text-4xl text-gold leading-none mb-2">
                    {s.n}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-dim)]">
                    {s.l}
                  </div>
                </>
              ) : (
                <>
                  <div className="font-display text-xl md:text-2xl text-white leading-tight mb-1">
                    {s.l}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-dim)]">
                    Proven across
                  </div>
                </>
              )}
            </div>
          ))}
        </motion.div>
      </main>

      {/* Marquee at bottom */}
      <section
        data-testid="teaser-marquee"
        className="relative border-t border-line py-6 overflow-hidden bg-[var(--bg-elev)]"
      >
        <div className="teaser-marquee">
          {[...EXPERTISE, ...EXPERTISE, ...EXPERTISE].map((w, i) => (
            <span
              key={i}
              className="font-display text-2xl md:text-3xl px-8 whitespace-nowrap text-[var(--text-muted)]"
            >
              {w} <span className="text-gold mx-3">✦</span>
            </span>
          ))}
        </div>
      </section>

      {/* Bottom bar */}
      <footer
        data-testid="teaser-footer"
        className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-[10px] uppercase tracking-[0.3em] text-[var(--text-dim)]"
      >
        <div>© {new Date().getFullYear()} Rishikesh N · Bengaluru, IN</div>
        <div className="flex items-center gap-5">
          <a
            href={`mailto:${PROFILE.email}`}
            className="link-line hover:text-gold"
            data-testid="teaser-footer-email"
          >
            {PROFILE.email}
          </a>
          <a
            href={PROFILE.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="link-line hover:text-gold"
            data-testid="teaser-footer-linkedin"
          >
            LinkedIn
          </a>
        </div>
      </footer>
    </div>
  );
}
